"use client"

import * as React from "react"
import { MapPin, CloudSun, CloudRain, Sun, Cloud, CloudFog, CloudLightning, CloudSnow, Snowflake, Moon, RefreshCw } from "lucide-react"
import { useWeather } from "@/hooks/useWeather"

interface WeatherWidgetProps {
  weatherInfo?: string
  lang?: string
}

function WeatherIcon({ iconType, className = "size-4 text-blue-500" }: { iconType?: string; className?: string }) {
  switch (iconType) {
    case "Sun":
      return <Sun className={`size-4 text-amber-500 ${className}`} />
    case "Moon":
      return <Moon className={`size-4 text-indigo-300 ${className}`} />
    case "CloudSun":
      return <CloudSun className={`size-4 text-amber-500 ${className}`} />
    case "Cloud":
      return <Cloud className={`size-4 text-zinc-400 ${className}`} />
    case "CloudFog":
      return <CloudFog className={`size-4 text-zinc-400 ${className}`} />
    case "CloudRain":
    case "CloudDrizzle":
      return <CloudRain className={`size-4 text-blue-500 ${className}`} />
    case "CloudLightning":
      return <CloudLightning className={`size-4 text-amber-400 ${className}`} />
    case "CloudSnow":
    case "Snowflake":
      return <Snowflake className={`size-4 text-cyan-300 ${className}`} />
    default:
      return <CloudSun className={`size-4 text-amber-500 ${className}`} />
  }
}

export function WeatherWidget({ weatherInfo: propWeatherInfo, lang = "pt" }: WeatherWidgetProps) {
  const { weatherData, isLoading, error, isLocationGranted, requestLocation } = useWeather(lang)

  const isLocationActive = isLocationGranted && !!weatherData

  if (!isLocationActive) {
    return (
      <div className="text-center py-5 px-3 space-y-3 font-sans">
        <div className="size-12 rounded-2xl bg-blue-500/10 dark:bg-blue-500/20 text-[#004EB1] dark:text-blue-400 flex items-center justify-center mx-auto shadow-xs animate-bounce">
          <MapPin className="size-6 text-[#004EB1] dark:text-blue-400" />
        </div>
        <div className="space-y-1">
          <h4 className="font-bold text-xs text-zinc-900 dark:text-zinc-100">
            {lang === "pt" ? "Compartilhe sua localização" : lang === "es" ? "Comparte tu ubicación" : "Share your location"}
          </h4>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-[220px] mx-auto font-medium">
            {lang === "pt"
              ? "Permita o acesso à localização para exibir o clima, temperatura e previsão em tempo real da sua cidade."
              : lang === "es"
              ? "Permite el acceso a la ubicación para ver el clima y pronóstico en tiempo real."
              : "Allow location access to view real-time weather and forecast for your city."}
          </p>
        </div>

        {error && (
          <p className="text-[10px] text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-950/40 p-2 rounded-lg leading-tight font-medium">
            {error}
          </p>
        )}

        <button
          type="button"
          disabled={isLoading}
          onClick={() => requestLocation(true)}
          className="px-4 py-2 rounded-xl bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-bold transition-all shadow-sm cursor-pointer active:scale-95 disabled:opacity-50 flex items-center justify-center gap-1.5 mx-auto"
        >
          {isLoading && <RefreshCw className="size-3.5 animate-spin" />}
          <span>
            {isLoading
              ? lang === "pt"
                ? "Obtendo clima..."
                : "Obtaining..."
              : lang === "pt"
              ? "Ativar Clima Real"
              : lang === "es"
              ? "Activar Clima Real"
              : "Enable Live Weather"}
          </span>
        </button>
      </div>
    )
  }

  const metrics = {
    humidity: lang === "pt" ? "Umidade" : lang === "es" ? "Humedad" : "Humidity",
    wind: lang === "pt" ? "Vento" : lang === "es" ? "Viento" : "Wind",
    apparent: lang === "pt" ? "Sensação" : lang === "es" ? "Sensación" : "Feels Like",
    uv: lang === "pt" ? "Índice UV" : lang === "es" ? "Índice UV" : "UV Index",
    forecastTitle: lang === "pt" ? "Previsão de 5 Dias" : lang === "es" ? "Pronóstico de 5 Días" : "5-Day Forecast",
  }

  const uvDescription =
    weatherData.uvIndex <= 2
      ? lang === "pt" ? "Baixo" : "Low"
      : weatherData.uvIndex <= 5
      ? lang === "pt" ? "Moderado" : "Moderate"
      : weatherData.uvIndex <= 7
      ? lang === "pt" ? "Alto" : "High"
      : lang === "pt" ? "Muito Alto" : "Very High"

  return (
    <div className="space-y-4 font-sans text-xs">
      <div className="relative text-center py-2 border-b border-zinc-100 dark:border-zinc-800">
        <button
          type="button"
          onClick={() => requestLocation(false)}
          title="Atualizar clima"
          className="absolute right-0 top-1 p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors cursor-pointer rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          <RefreshCw className={`size-3.5 ${isLoading ? "animate-spin" : ""}`} />
        </button>

        <h4 className="font-bold text-sm text-zinc-900 dark:text-white leading-tight flex items-center justify-center gap-1">
          <MapPin className="size-3.5 text-[#004EB1] dark:text-blue-400" />
          <span>{weatherData.cityName}</span>
        </h4>
        <div className="flex items-center justify-center gap-2 mt-1">
          <span className="text-3xl font-extrabold text-[#004EB1] dark:text-blue-400 font-mono">
            {weatherData.currentTemp}°C
          </span>
          <span className="text-2xl select-none">{weatherData.conditionEmoji}</span>
        </div>
        <span className="text-[10px] text-zinc-500 dark:text-zinc-400 font-medium block mt-0.5">
          {weatherData.conditionLabel} • Mín: {weatherData.minTempToday}°C / Máx: {weatherData.maxTempToday}°C
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <div className="p-2 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-150 dark:border-zinc-850 flex flex-col justify-between">
          <span className="text-[9px] font-bold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider">
            {metrics.humidity}
          </span>
          <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200 mt-1">
            {weatherData.humidity}%
          </span>
        </div>
        <div className="p-2 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-150 dark:border-zinc-850 flex flex-col justify-between">
          <span className="text-[9px] font-bold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider">
            {metrics.wind}
          </span>
          <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200 mt-1">
            {weatherData.windSpeed} km/h
          </span>
        </div>
        <div className="p-2 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-150 dark:border-zinc-850 flex flex-col justify-between">
          <span className="text-[9px] font-bold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider">
            {metrics.apparent}
          </span>
          <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200 mt-1">
            {weatherData.apparentTemp}°C
          </span>
        </div>
        <div className="p-2 bg-zinc-50 dark:bg-zinc-950 rounded-xl border border-zinc-150 dark:border-zinc-850 flex flex-col justify-between">
          <span className="text-[9px] font-bold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider">
            {metrics.uv}
          </span>
          <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200 mt-1">
            {weatherData.uvIndex} ({uvDescription})
          </span>
        </div>
      </div>

      {weatherData.daily && weatherData.daily.length > 0 && (
        <div className="space-y-2">
          <h5 className="text-[10px] font-extrabold text-zinc-400 dark:text-zinc-550 uppercase tracking-wider">
            {metrics.forecastTitle}
          </h5>
          <div className="space-y-1 bg-zinc-50/50 dark:bg-zinc-950/30 p-2 rounded-xl border border-zinc-150/50 dark:border-zinc-850/50">
            {weatherData.daily.map((f, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-1.5 border-b border-zinc-100 dark:border-zinc-850/50 last:border-0 last:pb-0"
              >
                <span className="font-bold text-zinc-700 dark:text-zinc-300 w-9">{f.dayName}</span>
                <span className="flex items-center justify-center size-5 text-sm select-none" title={f.condition}>
                  {f.emoji}
                </span>
                <span className="text-[10px] text-zinc-500 dark:text-zinc-400 truncate flex-1 px-2 text-left">
                  {f.condition}
                </span>
                <span className="font-mono text-[10px] font-semibold text-zinc-800 dark:text-zinc-200 w-16 text-right tabular-nums">
                  {f.minTemp}° / {f.maxTemp}°
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
